import type { AABB } from "../math/aabb.js";
import type { Vec3 } from "../math/vec3.js";
import { validateMap } from "./queries.js";
import type {
  ContainerKind,
  ExtractDef,
  GlassPaneDef,
  LandKind,
  LootSpawnDef,
  MapAddress,
  MapBox,
  MapDef,
  MapPoi,
  MapProp,
  MapSign,
  MapTerrain,
  MaterialId,
  PoiKind,
  PropKind,
} from "./types.js";

/**
 * Moving a map from the server to a client. Three load-bearing things:
 * `serializeMapDef`, a CANONICAL JSON encoding (same map, same bytes, on any
 * machine); `mapHash`, a hash of those bytes the client checks against the one
 * `welcome` named, catching a stale or truncated download before it reaches
 * the collision world; `deserializeMapDef`, parse plus full structural
 * validation, treating the payload as untrusted. The hash is FNV-1a — not a
 * security primitive; 64 bits detects corruption and version skew.
 */

// ---------------------------------------------------------------------------
// Canonical serialization
// ---------------------------------------------------------------------------

const MATERIALS: readonly MaterialId[] = [
  "concrete",
  "wood",
  "metal",
  "dirt",
  "brick",
  "stone",
  "glass",
];

const PROP_KINDS: readonly PropKind[] = [
  "pine",
  "grass",
  "rock_small",
  "flowerbox",
  "broadleaf",
  "fruit_tree",
  "window",
  "door",
  "casement",
];

/** Mirrored in `mapgen/src/types.ts`; the two must stay in step. */
const CONTAINER_KINDS: readonly ContainerKind[] = [
  "crate",
  "cupboard",
  "shelf",
  "toolbox",
  "fridge",
  "chest",
];

/** Mirrored in `mapgen/src/world.ts`, which decides which cells earn one. */
const LAND_KINDS: readonly LandKind[] = ["meadow", "farmland", "forest", "orchard"];

const POI_KINDS: readonly PoiKind[] = [
  "restaurant",
  "cafe",
  "shop",
  "hotel",
  "church",
  "school",
  "civic",
  "office",
  "fire_station",
  "landmark",
];

function canonicalVec(v: Vec3): Vec3 {
  return { x: v.x, y: v.y, z: v.z };
}

function canonicalBox(b: AABB): AABB {
  return { min: canonicalVec(b.min), max: canonicalVec(b.max) };
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, Math.min(bytes.length, i + chunk)));
  }
  return btoa(binary);
}

function base64ToBytes(text: string, what: string): Uint8Array {
  let binary: string;
  try {
    binary = atob(text);
  } catch {
    fail(`${what} is not valid base64`);
  }
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

function encodeTerrainHeights(
  heights: readonly number[],
): { offset: number; scale: number; bytesPerHeight: 2 | 4; data: string } {
  let offset = Infinity;
  for (const h of heights) if (h < offset) offset = h;
  if (offset === Infinity) offset = 0;
  const scale = 0.001;
  offset = Math.floor(offset * 1000) / 1000;
  let max = 0;
  for (const h of heights) max = Math.max(max, Math.round((h - offset) / scale));
  const bytesPerHeight: 2 | 4 = max <= 0xffff ? 2 : 4;
  const bytes = new Uint8Array(heights.length * bytesPerHeight);
  for (let i = 0; i < heights.length; i++) {
    const q = Math.round(((heights[i] as number) - offset) / scale);
    if (q < 0 || q > 0xffffffff) throw new Error("terrain height range exceeds compact encoding");
    const o = i * bytesPerHeight;
    bytes[o] = q & 0xff;
    bytes[o + 1] = (q >>> 8) & 0xff;
    if (bytesPerHeight === 4) {
      bytes[o + 2] = (q >>> 16) & 0xff;
      bytes[o + 3] = (q >>> 24) & 0xff;
    }
  }
  return { offset, scale, bytesPerHeight, data: bytesToBase64(bytes) };
}

function encodeSurfaceIds(ids: readonly number[]): { bytesPerId: 1 | 2; data: string } {
  let max = 0;
  for (const id of ids) if (id > max) max = id;
  if (max <= 0xff) return { bytesPerId: 1, data: bytesToBase64(Uint8Array.from(ids)) };
  if (max > 0xffff) throw new Error("terrain surface palette exceeds compact encoding");
  const bytes = new Uint8Array(ids.length * 2);
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i] as number;
    bytes[i * 2] = id & 0xff;
    bytes[i * 2 + 1] = id >>> 8;
  }
  return { bytesPerId: 2, data: bytesToBase64(bytes) };
}

/**
 * Rebuilds the map with every field in a fixed order and nothing else —
 * `JSON.stringify` walks own properties in insertion order, which is what makes
 * the encoding canonical.
 */
function canonicalMap(map: MapDef): Record<string, unknown> {
  const solids = map.solids.map((s) => {
    const out: Record<string, unknown> = {
      box: canonicalBox(s.box),
      material: s.material,
    };
    if (s.colorTint !== undefined) out["colorTint"] = s.colorTint;
    return out;
  });

  const visuals = (map.visuals ?? []).map((v) => {
    const out: Record<string, unknown> = { kind: v.kind, pos: canonicalVec(v.pos) };
    if (v.yaw !== undefined) out["yaw"] = v.yaw;
    if (v.scale !== undefined) out["scale"] = v.scale;
    return out;
  });

  const packedTerrain =
    map.terrain === undefined ? null : encodeTerrainHeights(map.terrain.heights);
  const packedSurfaces =
    map.terrain === undefined ? null : encodeSurfaceIds(map.terrain.surfaceIds);
  let terrain: Record<string, unknown> | undefined;
  if (map.terrain !== undefined) {
    terrain = {
      origin: { x: map.terrain.origin.x, z: map.terrain.origin.z },
      cellSize: map.terrain.cellSize,
      width: map.terrain.width,
      depth: map.terrain.depth,
      heightOffset: packedTerrain?.offset,
      heightScale: packedTerrain?.scale,
      heightBytes: packedTerrain?.bytesPerHeight,
      heightData: packedTerrain?.data,
      surfaceIndexBytes: packedSurfaces?.bytesPerId,
      surfaceData: packedSurfaces?.data,
      surfaces: map.terrain.surfaces.map((s) => {
        const out: Record<string, unknown> = { material: s.material };
        if (s.colorTint !== undefined) out["colorTint"] = s.colorTint;
        // Appended after the tint and only where there is one, so unlabelled
        // ground encodes to the bytes it always did.
        if (s.land !== undefined) out["land"] = s.land;
        return out;
      }),
    };
    // Appended after `surfaces`, only when non-empty, so a heightfield with no
    // holes keeps its `mapHash`.
    if (map.terrain.holes !== undefined && map.terrain.holes.length > 0) {
      terrain["holes"] = [...map.terrain.holes];
    }
  }
  return {
    id: map.id,
    name: map.name,
    bounds: canonicalBox(map.bounds),
    raidLengthSec: map.raidLengthSec,
    terrain,
    solids,
    playerSpawns: map.playerSpawns.map(canonicalVec),
    extracts: map.extracts.map((e) => ({
      id: e.id,
      name: e.name,
      zone: canonicalBox(e.zone),
      dwellSec: e.dwellSec,
    })),
    waypoints: {
      nodes: map.waypoints.nodes.map((n) => ({ id: n.id, pos: canonicalVec(n.pos) })),
      edges: map.waypoints.edges.map((e) => [e[0], e[1]]),
    },
    visuals,
    pois: (map.pois ?? []).map((p) => ({
      name: p.name,
      kind: p.kind,
      pos: canonicalVec(p.pos),
    })),
    // Appended last, so everything before keeps its bytes.
    lootSpawns: (map.lootSpawns ?? []).map((l) => ({
      id: l.id,
      kind: l.kind,
      table: l.table,
      box: canonicalBox(l.box),
    })),
    // Same rule again: new fields only ever append (`PROTOCOL_VERSION` 8).
    signs: (map.signs ?? []).map((s) => {
      const out: Record<string, unknown> = { text: s.text, kind: s.kind };
      if (s.poiKind !== undefined) out["poiKind"] = s.poiKind;
      out["pos"] = canonicalVec(s.pos);
      out["yaw"] = s.yaw;
      return out;
    }),
    // ...and once more, for the address book (`PROTOCOL_VERSION` 13).
    addresses: (map.addresses ?? []).map((a) => ({
      street: a.street,
      number: a.number,
      pos: canonicalVec(a.pos),
    })),
    // ...and the window panes (`PROTOCOL_VERSION` 34).
    glass: (map.glass ?? []).map((g) => ({ box: canonicalBox(g.box) })),
  };
}

/** The map as canonical JSON text — the exact bytes served over HTTP. */
export function serializeMapDef(map: MapDef): string {
  return JSON.stringify(canonicalMap(map));
}

// ---------------------------------------------------------------------------
// Hashing
// ---------------------------------------------------------------------------

/**
 * 64-bit FNV-1a over the canonical encoding, as 16 hex digits — two 32-bit
 * streams with `Math.imul` rather than BigInt, identical in node and every
 * browser. A cache key, an ETag, an integrity check; never security.
 */
export function hashString(text: string): string {
  let a = 0x811c9dc5;
  let b = 0x01000193;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x85ebca6b) >>> 0;
    // Cross-feed the two streams so they cannot drift into being one 32-bit
    // hash written twice.
    b = (b ^ (a >>> 13)) >>> 0;
  }
  return hex8(a) + hex8(b);
}

function hex8(v: number): string {
  return (v >>> 0).toString(16).padStart(8, "0");
}

/** Deterministic content hash of a map. Equal maps always hash equal. */
export function mapHash(map: MapDef): string {
  return hashString(serializeMapDef(map));
}

// ---------------------------------------------------------------------------
// Deserialization
// ---------------------------------------------------------------------------

export class MapDecodeError extends Error {
  readonly problems: readonly string[];
  constructor(message: string, problems: readonly string[] = []) {
    super(message);
    this.name = "MapDecodeError";
    this.problems = problems;
  }
}

function isObj(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

function fail(what: string): never {
  throw new MapDecodeError(`map payload: ${what}`);
}

function num(x: unknown, what: string): number {
  if (typeof x !== "number" || !Number.isFinite(x)) fail(`${what} is not a finite number`);
  return x;
}

function str(x: unknown, what: string): string {
  if (typeof x !== "string") fail(`${what} is not a string`);
  return x;
}

function arr(x: unknown, what: string): unknown[] {
  if (!Array.isArray(x)) fail(`${what} is not an array`);
  return x;
}

function vec3(x: unknown, what: string): Vec3 {
  if (!isObj(x)) fail(`${what} is not an object`);
  return { x: num(x["x"], `${what}.x`), y: num(x["y"], `${what}.y`), z: num(x["z"], `${what}.z`) };
}

function box(x: unknown, what: string): AABB {
  if (!isObj(x)) fail(`${what} is not an object`);
  return { min: vec3(x["min"], `${what}.min`), max: vec3(x["max"], `${what}.max`) };
}

/**
 * Parses a serialized map and validates it structurally. Accepts JSON text or a
 * parsed value. Throws `MapDecodeError` for a malformed payload OR a map that
 * fails `validateMap`.
 */
export function deserializeMapDef(json: unknown): MapDef {
  let parsed: unknown = json;
  if (typeof json === "string") {
    try {
      parsed = JSON.parse(json) as unknown;
    } catch (err) {
      throw new MapDecodeError(`map payload is not valid JSON: ${(err as Error).message}`);
    }
  }
  if (!isObj(parsed)) fail("top level is not an object");

  let terrain: MapTerrain | undefined;
  const terrainRaw = parsed["terrain"];
  if (terrainRaw !== undefined) {
    if (!isObj(terrainRaw)) fail("terrain is not an object");
    const originRaw = terrainRaw["origin"];
    if (!isObj(originRaw)) fail("terrain.origin is not an object");
    const width = num(terrainRaw["width"], "terrain.width");
    const depth = num(terrainRaw["depth"], "terrain.depth");
    if (!Number.isInteger(width) || !Number.isInteger(depth)) {
      fail("terrain dimensions are not integers");
    }
    let heights: number[];
    if (typeof terrainRaw["heightData"] === "string") {
      const bytes = base64ToBytes(terrainRaw["heightData"], "terrain.heightData");
      const offset = num(terrainRaw["heightOffset"], "terrain.heightOffset");
      const scale = num(terrainRaw["heightScale"], "terrain.heightScale");
      const heightBytes =
        terrainRaw["heightBytes"] === undefined
          ? 2
          : num(terrainRaw["heightBytes"], "terrain.heightBytes");
      if (heightBytes !== 2 && heightBytes !== 4) fail("terrain.heightBytes must be 2 or 4");
      if (bytes.length % heightBytes !== 0) fail("terrain.heightData has a truncated sample");
      heights = new Array<number>(bytes.length / heightBytes);
      for (let i = 0; i < heights.length; i++) {
        const o = i * heightBytes;
        let q = (bytes[o] as number) | ((bytes[o + 1] as number) << 8);
        if (heightBytes === 4) {
          q =
            (q | ((bytes[o + 2] as number) << 16) | ((bytes[o + 3] as number) << 24)) >>> 0;
        }
        heights[i] = Math.round((offset + q * scale) * 1000) / 1000;
      }
    } else {
      heights = arr(terrainRaw["heights"], "terrain.heights").map((v, i) =>
        num(v, `terrain.heights[${i}]`),
      );
    }
    let surfaceIds: number[];
    if (typeof terrainRaw["surfaceData"] === "string") {
      const bytes = base64ToBytes(terrainRaw["surfaceData"], "terrain.surfaceData");
      const bytesPerId =
        terrainRaw["surfaceIndexBytes"] === undefined
          ? 1
          : num(terrainRaw["surfaceIndexBytes"], "terrain.surfaceIndexBytes");
      if (bytesPerId !== 1 && bytesPerId !== 2) {
        fail("terrain.surfaceIndexBytes must be 1 or 2");
      }
      if (bytes.length % bytesPerId !== 0) fail("terrain.surfaceData has a truncated id");
      surfaceIds = new Array<number>(bytes.length / bytesPerId);
      for (let i = 0; i < surfaceIds.length; i++) {
        surfaceIds[i] =
          bytesPerId === 1
            ? (bytes[i] as number)
            : (bytes[i * 2] as number) | ((bytes[i * 2 + 1] as number) << 8);
      }
    } else {
      surfaceIds = arr(terrainRaw["surfaceIds"], "terrain.surfaceIds").map((v, i) => {
        const id = num(v, `terrain.surfaceIds[${i}]`);
        if (!Number.isInteger(id)) fail(`terrain.surfaceIds[${i}] is not an integer`);
        return id;
      });
    }
    const surfaces = arr(terrainRaw["surfaces"], "terrain.surfaces").map((s, i) => {
      if (!isObj(s)) fail(`terrain.surfaces[${i}] is not an object`);
      const material = str(s["material"], `terrain.surfaces[${i}].material`);
      if (!MATERIALS.includes(material as MaterialId)) {
        fail(`terrain.surfaces[${i}].material "${material}" is unknown`);
      }
      const out: MapTerrain["surfaces"][number] = { material: material as MaterialId };
      if (s["colorTint"] !== undefined) {
        out.colorTint = num(s["colorTint"], `terrain.surfaces[${i}].colorTint`);
      }
      // Optional, checked against the closed set like `material`.
      if (s["land"] !== undefined) {
        const land = str(s["land"], `terrain.surfaces[${i}].land`);
        if (!LAND_KINDS.includes(land as LandKind)) {
          fail(`terrain.surfaces[${i}].land "${land}" is unknown`);
        }
        out.land = land as LandKind;
      }
      return out;
    });
    // Optional and dropped again if empty, so a map that never had holes
    // decodes
    // with `holes` undefined and compares equal to the encoded one.
    let holes: number[] | undefined;
    if (terrainRaw["holes"] !== undefined) {
      const decoded = arr(terrainRaw["holes"], "terrain.holes").map((v, i) => {
        const q = num(v, `terrain.holes[${i}]`);
        if (!Number.isInteger(q)) fail(`terrain.holes[${i}] is not an integer`);
        return q;
      });
      if (decoded.length > 0) holes = decoded;
    }
    terrain = {
      origin: {
        x: num(originRaw["x"], "terrain.origin.x"),
        z: num(originRaw["z"], "terrain.origin.z"),
      },
      cellSize: num(terrainRaw["cellSize"], "terrain.cellSize"),
      width,
      depth,
      heights,
      surfaceIds,
      surfaces,
    };
    if (holes !== undefined) terrain.holes = holes;
  }

  const solids: MapBox[] = arr(parsed["solids"], "solids").map((s, i) => {
    if (!isObj(s)) fail(`solids[${i}] is not an object`);
    const material = str(s["material"], `solids[${i}].material`);
    if (!MATERIALS.includes(material as MaterialId)) {
      fail(`solids[${i}].material "${material}" is unknown`);
    }
    const out: MapBox = {
      box: box(s["box"], `solids[${i}].box`),
      material: material as MaterialId,
    };
    if (s["colorTint"] !== undefined) {
      out.colorTint = num(s["colorTint"], `solids[${i}].colorTint`);
    }
    return out;
  });

  const extracts: ExtractDef[] = arr(parsed["extracts"], "extracts").map((e, i) => {
    if (!isObj(e)) fail(`extracts[${i}] is not an object`);
    return {
      id: str(e["id"], `extracts[${i}].id`),
      name: str(e["name"], `extracts[${i}].name`),
      zone: box(e["zone"], `extracts[${i}].zone`),
      dwellSec: num(e["dwellSec"], `extracts[${i}].dwellSec`),
    };
  });

  const wpRaw = parsed["waypoints"];
  if (!isObj(wpRaw)) fail("waypoints is not an object");
  const nodes = arr(wpRaw["nodes"], "waypoints.nodes").map((n, i) => {
    if (!isObj(n)) fail(`waypoints.nodes[${i}] is not an object`);
    const id = num(n["id"], `waypoints.nodes[${i}].id`);
    if (!Number.isInteger(id)) fail(`waypoints.nodes[${i}].id is not an integer`);
    return { id, pos: vec3(n["pos"], `waypoints.nodes[${i}].pos`) };
  });
  const edges = arr(wpRaw["edges"], "waypoints.edges").map((e, i) => {
    const pair = arr(e, `waypoints.edges[${i}]`);
    if (pair.length !== 2) fail(`waypoints.edges[${i}] is not a pair`);
    return [
      num(pair[0], `waypoints.edges[${i}][0]`),
      num(pair[1], `waypoints.edges[${i}][1]`),
    ] as [number, number];
  });

  const visualsRaw = parsed["visuals"];
  const visuals: MapProp[] =
    visualsRaw === undefined
      ? []
      : arr(visualsRaw, "visuals").map((v, i) => {
          if (!isObj(v)) fail(`visuals[${i}] is not an object`);
          const kind = str(v["kind"], `visuals[${i}].kind`);
          if (!PROP_KINDS.includes(kind as PropKind)) {
            fail(`visuals[${i}].kind "${kind}" is unknown`);
          }
          const out: MapProp = {
            kind: kind as PropKind,
            pos: vec3(v["pos"], `visuals[${i}].pos`),
          };
          if (v["yaw"] !== undefined) out.yaw = num(v["yaw"], `visuals[${i}].yaw`);
          if (v["scale"] !== undefined) out.scale = num(v["scale"], `visuals[${i}].scale`);
          return out;
        });

  const poisRaw = parsed["pois"];
  const pois: MapPoi[] =
    poisRaw === undefined
      ? []
      : arr(poisRaw, "pois").map((p, i) => {
          if (!isObj(p)) fail(`pois[${i}] is not an object`);
          const kind = str(p["kind"], `pois[${i}].kind`);
          if (!POI_KINDS.includes(kind as PoiKind)) {
            fail(`pois[${i}].kind "${kind}" is unknown`);
          }
          return {
            name: str(p["name"], `pois[${i}].name`),
            kind: kind as PoiKind,
            pos: vec3(p["pos"], `pois[${i}].pos`),
          };
        });

  const lootRaw = parsed["lootSpawns"];
  const lootSpawns: LootSpawnDef[] =
    lootRaw === undefined
      ? []
      : arr(lootRaw, "lootSpawns").map((l, i) => {
          if (!isObj(l)) fail(`lootSpawns[${i}] is not an object`);
          const kind = str(l["kind"], `lootSpawns[${i}].kind`);
          if (!CONTAINER_KINDS.includes(kind as ContainerKind)) {
            fail(`lootSpawns[${i}].kind "${kind}" is unknown`);
          }
          const table = str(l["table"], `lootSpawns[${i}].table`);
          // A non-empty STRING only: which tables exist is content the map
          // layer
          // knows nothing about.
          if (table.length === 0) fail(`lootSpawns[${i}].table is empty`);
          const id = str(l["id"], `lootSpawns[${i}].id`);
          if (id.length === 0) fail(`lootSpawns[${i}].id is empty`);
          return { id, kind: kind as ContainerKind, table, box: box(l["box"], `lootSpawns[${i}].box`) };
        });

  const signsRaw = parsed["signs"];
  const signs: MapSign[] =
    signsRaw === undefined
      ? []
      : arr(signsRaw, "signs").map((s, i) => {
          if (!isObj(s)) fail(`signs[${i}] is not an object`);
          const text = str(s["text"], `signs[${i}].text`);
          if (text.length === 0) fail(`signs[${i}].text is empty`);
          const kind = str(s["kind"], `signs[${i}].kind`);
          if (kind !== "street" && kind !== "poi") fail(`signs[${i}].kind "${kind}" is unknown`);
          const out: MapSign = {
            text,
            kind,
            pos: vec3(s["pos"], `signs[${i}].pos`),
            yaw: num(s["yaw"], `signs[${i}].yaw`),
          };
          if (s["poiKind"] !== undefined) {
            const pk = str(s["poiKind"], `signs[${i}].poiKind`);
            if (!POI_KINDS.includes(pk as PoiKind)) {
              fail(`signs[${i}].poiKind "${pk}" is unknown`);
            }
            out.poiKind = pk as PoiKind;
          }
          return out;
        });

  const addressesRaw = parsed["addresses"];
  const addresses: MapAddress[] =
    addressesRaw === undefined
      ? []
      : arr(addressesRaw, "addresses").map((a, i) => {
          if (!isObj(a)) fail(`addresses[${i}] is not an object`);
          const street = str(a["street"], `addresses[${i}].street`);
          if (street.length === 0) fail(`addresses[${i}].street is empty`);
          // A house NUMBER is a string ("17a"), copied verbatim from OSM.
          const number = str(a["number"], `addresses[${i}].number`);
          if (number.length === 0) fail(`addresses[${i}].number is empty`);
          return { street, number, pos: vec3(a["pos"], `addresses[${i}].pos`) };
        });

  const glassRaw = parsed["glass"];
  const glass: GlassPaneDef[] =
    glassRaw === undefined
      ? []
      : arr(glassRaw, "glass").map((g, i) => {
          if (!isObj(g)) fail(`glass[${i}] is not an object`);
          return { box: box(g["box"], `glass[${i}].box`) };
        });

  const map: MapDef = {
    id: str(parsed["id"], "id"),
    name: str(parsed["name"], "name"),
    bounds: box(parsed["bounds"], "bounds"),
    raidLengthSec: num(parsed["raidLengthSec"], "raidLengthSec"),
    ...(terrain === undefined ? {} : { terrain }),
    solids,
    playerSpawns: arr(parsed["playerSpawns"], "playerSpawns").map((p, i) =>
      vec3(p, `playerSpawns[${i}]`),
    ),
    extracts,
    waypoints: { nodes, edges },
    visuals,
    pois,
    signs,
    addresses,
    lootSpawns,
    glass,
  };

  const problems = validateMap(map);
  if (problems.length > 0) {
    throw new MapDecodeError(
      `map "${map.id}" failed validation (${problems.length} problem(s)): ${problems.slice(0, 3).join("; ")}`,
      problems,
    );
  }
  return map;
}
